import { useEffect, useState } from 'react';

/**
 * Subscribe to a CSS media query. Returns the current match state and updates
 * on change. Mobile-first default (`false`) when `matchMedia` is unavailable
 * (SSR / non-DOM test environments), so the responsive grid degrades to the
 * single-column stacked layout rather than throwing.
 *
 * Used by Dashboard.tsx for the ≷1280px three-column ↔ stacked switch (DASH.B1).
 */
export function useMediaQuery(query: string): boolean {
  const read = (): boolean =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(query).matches
      : false;

  const [matches, setMatches] = useState<boolean>(read);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(query);
    // Update only from the subscription callback — the initial value is already
    // read by the useState initializer above (same render tick). `query` is a
    // stable constant for this app, so no synchronous re-sync is needed here
    // (which also keeps us clear of react-hooks/set-state-in-effect).
    const handler = (e: MediaQueryListEvent): void => setMatches(e.matches);

    // addEventListener is the modern API; addListener is the Safari < 14 /
    // legacy fallback. Guard both so the hook works across environments.
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', handler);
      return () => mql.removeEventListener('change', handler);
    }
    mql.addListener(handler);
    return () => mql.removeListener(handler);
  }, [query]);

  return matches;
}
